import { Link } from "react-router-dom";
import { getAbsoluteUrl } from "@/components/feature/Seo";

export type BreadcrumbItem = { label: string; to?: string };

/**
 * Builds a schema.org BreadcrumbList from the exact same items used by the
 * visual breadcrumb trail, so the trail also qualifies for Google breadcrumb
 * rich results. The final item (the current page) resolves to `currentUrl`.
 */
export const buildBreadcrumbSchema = (
  items: BreadcrumbItem[],
  currentUrl?: string,
) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => {
    const url = item.to ? getAbsoluteUrl(item.to) : currentUrl;
    return {
      "@type": "ListItem",
      position: index + 1,
      name: item.label,
      ...(url ? { item: url } : {}),
    };
  }),
});

/**
 * Breadcrumb trail for detail pages. Designed to sit on top of a dark hero
 * image, so it renders inside a subtle frosted pill for clear readability.
 */
export default function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-6">
      <ol className="inline-flex items-center gap-1.5 flex-wrap rounded-full bg-black/25 backdrop-blur-sm border border-background-50/15 px-3 py-1.5">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isLink = Boolean(item.to) && !isLast;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-1.5">
              {isLink ? (
                <Link
                  to={item.to as string}
                  className="text-xs md:text-sm text-background-100 hover:text-background-50 transition-colors whitespace-nowrap"
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? "page" : undefined}
                  className={`text-xs md:text-sm max-w-[55vw] truncate ${
                    isLast
                      ? "text-background-50 font-semibold"
                      : "text-background-100"
                  }`}
                >
                  {item.label}
                </span>
              )}
              {!isLast && (
                <i className="ri-arrow-right-s-line text-background-200 text-sm"></i>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}